import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { generateHash, HASH_AUTO_START_LEN } from "shared";
import { createLink } from "@/api/links";
import { ApiException } from "@/api/client";
import { copyText, shortUrl } from "@/lib/format";

/** UI-safe auto hash — generateHash throws only on absurd reserved collision. */
function suggestHash(): string {
	try {
		return generateHash(HASH_AUTO_START_LEN);
	} catch {
		// Fallback: empty leaves allocation to the server on save.
		return "";
	}
}

export function LinkNewPage() {
	const [params] = useSearchParams();
	const navigate = useNavigate();

	const [targetUrl, setTargetUrl] = useState(params.get("url") ?? "");
	// ?hash= prefill ("Make link" from /logs) marks the hash as custom.
	const [hash, setHash] = useState(() => params.get("hash") ?? suggestHash());
	const [custom, setCustom] = useState(() => params.get("hash") !== null);
	const [startsAt, setStartsAt] = useState("");
	const [expiresAt, setExpiresAt] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [created, setCreated] = useState<{
		hash: string;
		reassigned: boolean;
	} | null>(null);
	const [copied, setCopied] = useState(false);

	const preview = useMemo(() => (hash ? shortUrl(hash) : ""), [hash]);

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setBusy(true);
		try {
			const res = await createLink({
				targetUrl: targetUrl.trim(),
				hash: hash.trim() || undefined,
				isCustom: custom,
				startsAt: startsAt || null,
				expiresAt: expiresAt || null,
			});
			setCreated({
				hash: res.link.hash,
				reassigned: res.reassigned,
			});
		} catch (err) {
			if (err instanceof ApiException && err.code === "HASH_TAKEN") {
				setError("That hash is live. Disable/expire it first, or pick another.");
			} else {
				setError(err instanceof ApiException ? err.message : "Create failed");
			}
		} finally {
			setBusy(false);
		}
	}

	if (created) {
		const url = shortUrl(created.hash);
		return (
			<div className="mx-auto max-w-lg space-y-6">
				<div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
					<p className="text-sm text-fg-muted">
						{created.reassigned
							? "Hash reassigned to new target"
							: "Short link created"}
					</p>
					<p className="mt-2 font-mono text-lg text-accent break-all">{url}</p>
					<div className="mt-4 flex flex-wrap justify-center gap-2">
						<button
							type="button"
							className="rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600"
							onClick={async () => {
								const ok = await copyText(url);
								if (ok) {
									setCopied(true);
									setTimeout(() => setCopied(false), 1500);
								}
							}}
						>
							{copied ? "Copied!" : "Copy"}
						</button>
						<button
							type="button"
							className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
							onClick={() => navigate("/links")}
						>
							All links
						</button>
						<button
							type="button"
							className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-raised"
							onClick={() => {
								setCreated(null);
								setTargetUrl("");
								setHash(suggestHash());
								setCustom(false);
							}}
						>
							Create another
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-lg space-y-6">
			<div>
				<h1 className="text-xl font-semibold tracking-tight">New short link</h1>
				<p className="mt-1 text-sm text-fg-muted">
					Bookmarklet-friendly create form.{" "}
					<Link to="/links" className="text-accent hover:underline">
						Back
					</Link>
				</p>
			</div>

			{error && (
				<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
					{error}
				</div>
			)}

			<form
				onSubmit={onSubmit}
				className="space-y-4 rounded-xl border border-border bg-surface-raised p-5"
			>
				<label className="block text-sm">
					<span className="text-fg-muted">Target URL</span>
					<input
						type="url"
						required
						value={targetUrl}
						onChange={(e) => setTargetUrl(e.target.value)}
						placeholder="https://…"
						className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
					/>
				</label>

				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<span className="text-sm text-fg-muted">Hash</span>
						<label className="flex items-center gap-2 text-xs text-fg-muted">
							<input
								type="checkbox"
								checked={custom}
								onChange={(e) => setCustom(e.target.checked)}
							/>
							Custom
						</label>
					</div>
					<div className="flex gap-2">
						<input
							type="text"
							value={hash}
							onChange={(e) => {
								setHash(e.target.value);
								setCustom(true);
							}}
							pattern="[A-Za-z0-9]{3,32}"
							className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-accent/40"
						/>
						<button
							type="button"
							className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface"
							onClick={() => {
								setHash(suggestHash());
								setCustom(false);
							}}
						>
							Regenerate
						</button>
					</div>
					<p className="font-mono text-xs text-fg-muted break-all">{preview}</p>
					<p className="text-xs text-fg-muted">
						Dead custom hashes are reassigned (history kept). Live hashes return
						HASH_TAKEN.
					</p>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<label className="block text-sm">
						<span className="text-fg-muted">Starts at (UTC)</span>
						<input
							type="datetime-local"
							value={startsAt}
							onChange={(e) =>
								setStartsAt(
									e.target.value ? e.target.value.replace("T", " ") + ":00" : "",
								)
							}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
					<label className="block text-sm">
						<span className="text-fg-muted">Expires at (UTC)</span>
						<input
							type="datetime-local"
							value={expiresAt}
							onChange={(e) =>
								setExpiresAt(
									e.target.value ? e.target.value.replace("T", " ") + ":00" : "",
								)
							}
							className="mt-1 w-full rounded-lg border border-border bg-surface px-2 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/40"
						/>
					</label>
				</div>

				<button
					type="submit"
					disabled={busy}
					className="w-full rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
				>
					{busy ? "Saving…" : "Create short link"}
				</button>
			</form>
		</div>
	);
}
